import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Capacitor } from '@capacitor/core';
import { Copy, Trash2, Link as LinkIcon, Eye } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import ChoiceGroup from '../ui/ChoiceGroup';
import EmptyState from '../ui/EmptyState.jsx';
import Skeleton from '../ui/Skeleton';
import { apiService } from '../../services/api';
import { toast } from '../notifications';
import { buildShareUrl } from './journalUtils';

/**
 * Lernlogbuch per Link teilen — z.B. für ein Lerngespräch mit der Lehrkraft.
 *
 * Geteilt wird immer das GANZE Logbuch (Sessions + eigene Einträge), aufgelöst
 * beim Öffnen des Links: Was bis zum Gespräch dazukommt, ist automatisch dabei.
 * Der Link zeigt nur eine schreibgeschützte Ansicht ohne Dateien; wer nichts
 * mehr teilen will, löscht ihn hier. Kein @capacitor/share: Die Web-Share-API
 * deckt iOS ab, Android-WebView fällt auf die Zwischenablage zurück.
 */
const EXPIRY_OPTIONS = [
  { value: '7d', label: '7 Tage' },
  { value: '30d', label: '30 Tage' },
];

const formatDate = (value) => new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const ShareJournalDialog = ({ open, onClose }) => {
  const [expiresIn, setExpiresIn] = useState('7d');
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingToken, setDeletingToken] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    apiService.listJournalShares()
      .then((response) => {
        if (!cancelled && response?.success) setShares(response.data || []);
      })
      .catch(() => {
        if (!cancelled) toast.error('Links konnten nicht geladen werden', { haptic: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const copyOrShare = async (share) => {
    const url = buildShareUrl(share, { isNative: Capacitor.isNativePlatform() });
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'Mein Lernlogbuch', url });
        return;
      } catch (error) {
        // Abbruch durch den Nutzer ist kein Fehler; alles andere → Zwischenablage
        if (error?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopiert');
    } catch {
      toast.error('Kopieren nicht möglich — bitte den Link markieren und kopieren');
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await apiService.createJournalShare({ expiresIn });
      if (response?.success) {
        const created = { ...response.data, accessCount: 0, lastAccess: null, createdAt: new Date().toISOString() };
        setShares((prev) => [created, ...prev]);
        await copyOrShare(created);
      } else {
        toast.error('Link konnte nicht erstellt werden');
      }
    } catch {
      toast.error('Link konnte nicht erstellt werden');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (token) => {
    setDeletingToken(token);
    try {
      await apiService.deleteJournalShare(token);
      setShares((prev) => prev.filter((s) => s.token !== token));
    } catch {
      toast.error('Link konnte nicht gelöscht werden');
    } finally {
      setDeletingToken(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Logbuch teilen"
      size="md"
      footer={(
        <Button onClick={handleCreate} disabled={creating} fullWidth>
          <LinkIcon className="w-4 h-4" aria-hidden="true" />
          {creating ? 'Link wird erstellt…' : 'Link erzeugen'}
        </Button>
      )}
    >
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Wer den Link hat, sieht dein ganzes Logbuch — Sessions und eigene Einträge —
          schreibgeschützt und ohne Anmeldung. Dateien werden nicht mitgeteilt.
          Praktisch für ein Lerngespräch mit deiner Lehrkraft.
        </p>

        <ChoiceGroup
          label="Gültig für"
          value={expiresIn}
          onChange={setExpiresIn}
          options={EXPIRY_OPTIONS}
          columns={2}
        />

        <section aria-labelledby="share-links-heading">
          <h3 id="share-links-heading" className="text-sm font-semibold text-foreground mb-2">Aktive Links</h3>

          {loading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton.Row />
              <Skeleton.Row />
            </div>
          ) : shares.length === 0 ? (
            <EmptyState
              icon={LinkIcon}
              size="sm"
              className="rounded-xl border border-border bg-surface-raised p-3"
              text="Noch kein Link erstellt."
            />
          ) : (
            <ul className="space-y-2">
              {shares.map((share) => (
                <li key={share.token} className="flex items-center gap-3 rounded-xl border border-border bg-surface-raised p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone="neutral">gültig bis {formatDate(share.expiresAt)}</Badge>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                        {share.accessCount === 1 ? '1 Aufruf' : `${share.accessCount || 0} Aufrufe`}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground truncate" title={buildShareUrl(share)}>
                      {buildShareUrl(share)}
                    </p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => copyOrShare(share)} aria-label="Link kopieren">
                    <Copy className="w-4 h-4" aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(share.token)}
                    disabled={deletingToken === share.token}
                    aria-label="Link löschen"
                  >
                    <Trash2 className="w-4 h-4 text-danger" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
};

ShareJournalDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ShareJournalDialog;
